/**
 * TranslationExportWrapper — Entry point cho cả Internal & Commercial.
 *
 * Luôn render V2 (rich text editor). KHÔNG fallback, KHÔNG build data từ sourceText.
 * File legacy thiếu backend parse data → báo lỗi, user phải re-upload.
 */

import React, { useEffect, useRef, useState } from 'react';
import { AlertTriangle, RefreshCw } from 'lucide-react';
import TranslationExportV2 from './TranslationExportV2';
import { TranslationFile, AuditDetails } from '../../../types';
import { ensureFileContentLoaded } from '../../../hooks/useFileSystem';

interface TranslationExportWrapperProps {
    file: TranslationFile;
    onBackToSource: () => void;
    onNewSession: () => void;
    onRetranslate?: () => void;
    isRetranslating?: boolean;
    onUpdateAiOutput?: (newAiOutput: string[]) => void;
    onUpdateQualityResult?: (newResult: AuditDetails) => void;
    onRetryBatch?: (batchIndex: number) => void;
    onRetryAllFailed?: () => void;
    onRunQualityCheck?: () => void;
    isQualityChecking?: boolean;
    qcOutputLang?: string;
    onQcOutputLangChange?: (lang: string) => void;
    projectSourceLang?: string;
    onShowDebug?: () => void;
}

const TranslationExportWrapper: React.FC<TranslationExportWrapperProps> = ({
    file,
    onBackToSource,
    onNewSession,
    onRetranslate,
    isRetranslating = false,
    onUpdateAiOutput,
    onUpdateQualityResult,
    onRetryBatch,
    onRetryAllFailed,
    onRunQualityCheck,
    isQualityChecking = false,
    qcOutputLang,
    onQcOutputLangChange,
    projectSourceLang,
    onShowDebug,
}) => {
    const [isLoadingContent, setIsLoadingContent] = useState(false);
    const attemptedLazyLoadRef = useRef<string | null>(null);

    useEffect(() => {
        const needsLazyContent = !file.originalFile && !file.backendFileId;
        if (!needsLazyContent || isLoadingContent || attemptedLazyLoadRef.current === file.id) return;

        let cancelled = false;
        attemptedLazyLoadRef.current = file.id;
        setIsLoadingContent(true);
        ensureFileContentLoaded(file.id, true)
            .finally(() => {
                if (!cancelled) setIsLoadingContent(false);
            });
        return () => {
            cancelled = true;
        };
    }, [file.id, file.contentLoaded, file.originalFile, file.backendFileId, isLoadingContent]);

    // V2 hard requirements — thiếu 1 field nào báo lỗi cụ thể
    const missing: string[] = [];
    if (!file.backendParagraphs) missing.push('backendParagraphs (parse result)');
    if (!file.backendSegments) missing.push('backendSegments (source segments)');
    if (!file.backendFileId && !file.originalFile) missing.push('backendFileId hoặc originalFile (file buffer — mất sau reload nếu không connect thư mục)');

    if (isLoadingContent) {
        return (
            <div className="h-full flex items-center justify-center p-6">
                <div className="max-w-xl bg-white border border-slate-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-center gap-3">
                        <RefreshCw className="w-5 h-5 text-slate-500 animate-spin" />
                        <div>
                            <h3 className="text-base font-bold text-slate-800">Đang tải lại dữ liệu file</h3>
                            <p className="text-sm text-slate-500 mt-1">App đang đọc file gốc từ thư mục đã kết nối.</p>
                        </div>
                    </div>
                </div>
            </div>
        );
    }

    if (missing.length > 0) {
        return (
            <div className="h-full flex items-center justify-center p-6">
                <div className="max-w-xl bg-red-50 border border-red-200 rounded-xl p-6 shadow-sm">
                    <div className="flex items-start gap-3 mb-4">
                        <AlertTriangle className="w-6 h-6 text-red-600 shrink-0 mt-0.5" />
                        <div>
                            <h3 className="text-base font-bold text-red-800 mb-1">Không thể mở file</h3>
                            <p className="text-sm text-red-700 leading-relaxed">
                                File <span className="font-mono font-bold">{file.fileName}</span> thiếu backend parse metadata.
                                File legacy (parse trước khi bật backend parser) hoặc reload làm mất in-memory data.
                                Vui lòng upload lại file gốc để parse.
                            </p>
                        </div>
                    </div>
                    <div className="bg-white border border-red-100 rounded-lg p-3 mb-4">
                        <div className="text-[10px] font-black uppercase tracking-widest text-red-500 mb-1">Thiếu:</div>
                        <ul className="text-xs text-slate-700 space-y-0.5">
                            {missing.map(m => (
                                <li key={m} className="flex items-center gap-1.5">
                                    <span className="w-1 h-1 rounded-full bg-red-400" />
                                    <code>{m}</code>
                                </li>
                            ))}
                        </ul>
                    </div>
                    <div className="flex gap-2">
                        <button
                            onClick={onBackToSource}
                            className="flex-1 px-4 py-2 rounded-lg text-sm font-semibold bg-white border border-slate-300 text-slate-700 hover:bg-slate-50"
                        >
                            ← Quay lại
                        </button>
                        {onRetranslate && (
                            <button
                                onClick={onRetranslate}
                                disabled={isRetranslating}
                                className="flex-1 flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-sm font-semibold bg-amber-600 text-white hover:bg-amber-700 disabled:opacity-60"
                            >
                                <RefreshCw className={`w-4 h-4 ${isRetranslating ? 'animate-spin' : ''}`} />
                                Dịch lại
                            </button>
                        )}
                    </div>
                </div>
            </div>
        );
    }

    return (
        <TranslationExportV2
            aiOutputSegments={file.aiOutput || []}
            fileName={file.fileName}
            qualityResult={file.auditResult}
            isQualityChecking={isQualityChecking}
            onRunQualityCheck={onRunQualityCheck}
            qcOutputLang={qcOutputLang}
            onQcOutputLangChange={onQcOutputLangChange}
            projectSourceLang={projectSourceLang}
            segmentEvaluations={file.auditResult?.segmentMappings}
            overallScore={file.auditResult?.overallScore}
            accuracyAnalysis={file.auditResult?.accuracyAnalysis}
            terminologyAnalysis={file.auditResult?.terminologyAnalysis}
            toneAnalysis={file.auditResult?.toneAnalysis}
            componentScores={file.auditResult?.componentScores}
            suggestedPromptUpdate={file.auditResult?.suggestedPromptUpdate}
            isTruncated={file.auditResult?.isTruncated}
            batchInfo={file.batchInfo}
            matchedRules={file.matchedRules}
            onRetryBatch={onRetryBatch}
            onRetryAllFailed={onRetryAllFailed}
            onRetranslate={onRetranslate}
            isRetranslating={isRetranslating}
            onUpdateAiOutput={onUpdateAiOutput}
            onUpdateQualityResult={onUpdateQualityResult}
            onBackToSource={onBackToSource}
            onNewSession={onNewSession}
            onShowDebug={onShowDebug}
            sourceSegments={file.backendSegments!}
            backendParagraphs={file.backendParagraphs!}
            backendFileId={file.backendFileId}
            originalFile={file.originalFile}
            originalFormat={file.originalFormat === 'docx' || file.originalFormat === 'pptx' ? file.originalFormat : undefined}
            backendChartLabels={file.backendChartLabels}
            backendChartAiStart={file.backendChartAiStart}
            backendSmartLabels={file.backendSmartLabels}
            backendSmartAiStart={file.backendSmartAiStart}
            backendParseStructure={file.backendParseStructure}
            backendSegments={file.backendSegments}
        />
    );
};

export default TranslationExportWrapper;
